import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("verifying");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return undefined;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch (e) {
        // keep polling — webhook/verification may lag
      }
      attempts += 1;
      if (attempts < 10) {
        timer = setTimeout(poll, 2000);
      } else {
        setState("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="flex min-h-screen flex-col bg-bone">
      <header className="border-b border-ink/15">
        <div className="mx-auto flex max-w-6xl items-center px-6 py-5 md:px-10">
          <a href="/" data-testid="success-brand-link" className="flex items-center gap-3">
            <img
              src="/assets/wmwe-logo-dark.png"
              alt="WMWE logo"
              className="h-9 w-9 rounded-full border border-ink/15 bg-cream object-contain p-[3px]"
            />
            <span className="font-sans text-[11px] tracking-[0.35em] text-ink">
              RECON 2026 <span className="text-ink/40">// CONFIRMATION</span>
            </span>
          </a>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-20">
        <div className="w-full max-w-xl border border-ink/25 bg-manila p-10 text-center md:p-14">
          {state === "verifying" && (
            <p data-testid="success-verifying" className="animate-pulse font-sans text-[11px] tracking-[0.4em] text-ink/60">
              VERIFYING YOUR PAYMENT...
            </p>
          )}

          {state === "paid" && (
            <div data-testid="success-confirmed">
              <div className="inline-block -rotate-6 border-[3px] border-wine p-1.5">
                <div className="border border-wine px-6 py-3">
                  <p className="font-sans text-sm font-semibold tracking-[0.25em] text-wine">
                    ACCESS GRANTED
                  </p>
                </div>
              </div>
              <h1 className="mt-8 font-serif text-4xl font-medium tracking-tight text-ink md:text-5xl">
                Your seat is <span className="italic text-wine">secured.</span>
              </h1>
              {order && (
                <p className="mt-6 font-sans text-[11px] leading-relaxed tracking-[0.2em] text-ink/60">
                  {order.package_name?.toUpperCase()}
                  <br />
                  {order.quantity} SEAT{order.quantity > 1 ? "S" : ""} // ${order.amount?.toLocaleString()}
                </p>
              )}
              <p className="mt-6 text-sm leading-relaxed text-smoke">
                A confirmation from Stripe is on its way to your inbox. Your briefing materials and
                final details will follow as November approaches.
              </p>
            </div>
          )}

          {state === "pending" && (
            <div data-testid="success-pending">
              <h1 className="font-serif text-4xl font-medium tracking-tight text-ink">
                Payment <span className="italic text-wine">processing.</span>
              </h1>
              <p className="mt-6 text-sm leading-relaxed text-smoke">
                Your payment is being confirmed. If you received a Stripe receipt, your seat is
                secured — otherwise please contact us.
              </p>
            </div>
          )}

          {state === "error" && (
            <div data-testid="success-error">
              <h1 className="font-serif text-4xl font-medium tracking-tight text-ink">
                Invalid confirmation <span className="italic text-wine">link.</span>
              </h1>
              <p className="mt-6 text-sm leading-relaxed text-smoke">
                This confirmation link is missing its session. If you completed a payment, check
                your email for the Stripe receipt.
              </p>
            </div>
          )}

          <a
            href="/"
            data-testid="success-home-link"
            className="mt-10 inline-block border border-ink/30 px-8 py-3 font-sans text-[10px] tracking-[0.35em] text-ink transition-colors duration-300 hover:border-wine hover:text-wine"
          >
            ← RETURN TO THE BRIEFING
          </a>
        </div>
      </main>
    </div>
  );
}
